import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { FollowActionResult } from '../api/client';
import { OrganizerData, OrganizerDataMap } from '../api/transformations';
import { useFollowClient } from './FollowClientContext';

/**
 * react-query key for a follow-status READ. Keyed by the SORTED id set so the
 * query + its optimistic mutation share one cache entry regardless of input
 * ordering.
 */
export const followStatusQueryKey = (organizerIds: string[]) => [
    'follow-organizer',
    'status',
    [...organizerIds].sort(),
];

export interface UseFollowStatusOptions {
    /**
     * Seed data (e.g. from `getServerSideProps`). Mirrors eb-ui's
     * `organizerData` prop that pre-populated the store before the client
     * hydration fetch.
     */
    initialData?: OrganizerDataMap;
    /** Skip the fetch (e.g. no ids yet). */
    enabled?: boolean;
}

/**
 * Query the follow-status of a set of organizers.
 *
 * Replaces eb-ui `addFollow`/`FollowMarionette`'s `_hydrateOrganizerData`
 * (which called `getOrganizers` on mount and dumped the result into the redux/
 * shared store). Here react-query owns fetch + cache + loading state.
 */
export const useFollowStatus = (
    organizerIds: string[],
    options: UseFollowStatusOptions = {},
): UseQueryResult<OrganizerDataMap, Error> => {
    const client = useFollowClient();

    return useQuery<OrganizerDataMap, Error>({
        queryKey: followStatusQueryKey(organizerIds),
        queryFn: () => client.getOrganizersStatus(organizerIds),
        enabled: (options.enabled ?? true) && organizerIds.length > 0,
        initialData: options.initialData,
    });
};

export interface FollowMutationVariables {
    organizerId: string;
    action: 'follow' | 'unfollow';
}

export interface UseFollowMutationOptions {
    /**
     * The organizer-id set whose status cache should be optimistically updated.
     * Usually the SAME ids passed to `useFollowStatus` so the button/card
     * reflects the change immediately.
     */
    organizerIds: string[];
    /** Fired after a successful follow. */
    onFollowSuccess?: (organizerId: string) => void;
    /** Fired after a successful unfollow. */
    onUnfollowSuccess?: (organizerId: string) => void;
    /** Fired on any error (after optimistic rollback). */
    onError?: (variables: FollowMutationVariables, error: Error) => void;
}

/** Apply a follow/unfollow to a single organizer inside the status map. */
const applyFollowChange = (
    map: OrganizerDataMap | undefined,
    organizerId: string,
    followedByYou: boolean,
): OrganizerDataMap => {
    const current: OrganizerData = map?.[organizerId] ?? {
        id: organizerId,
        name: '',
        url: '',
    };
    const currentFollowers = current.numFollowers ?? 0;

    return {
        ...map,
        [organizerId]: {
            ...current,
            followedByYou,
            // Mirror eb-ui's optimistic count bump/clamp (addFollow._updateFollowState).
            numFollowers: followedByYou
                ? currentFollowers + 1
                : Math.max(currentFollowers - 1, 0),
        },
    };
};

/**
 * Follow/unfollow mutation with optimistic update + rollback.
 *
 * Replaces the eb-ui redux `setAction`/`_updateFollowState` + `redux-destination`
 * write thunks. On mutate we optimistically flip `followedByYou` and adjust
 * `numFollowers` in the react-query cache; on error we roll back to the snapshot
 * (the exact class of the audit bug — the button must not lie about state when a
 * POST fails).
 */
export const useFollowMutation = (
    options: UseFollowMutationOptions,
): {
    mutation: UseMutationResult<
        FollowActionResult,
        Error,
        FollowMutationVariables,
        { previous?: OrganizerDataMap }
    >;
    follow: (organizerId: string) => void;
    unfollow: (organizerId: string) => void;
} => {
    const client = useFollowClient();
    const queryClient = useQueryClient();
    const queryKey = followStatusQueryKey(options.organizerIds);

    const mutation = useMutation<
        FollowActionResult,
        Error,
        FollowMutationVariables,
        { previous?: OrganizerDataMap }
    >({
        mutationFn: ({ organizerId, action }) =>
            action === 'follow'
                ? client.followOrganizer(organizerId)
                : client.unfollowOrganizer(organizerId),

        onMutate: async ({ organizerId, action }) => {
            await queryClient.cancelQueries({ queryKey });
            const previous =
                queryClient.getQueryData<OrganizerDataMap>(queryKey);
            queryClient.setQueryData<OrganizerDataMap>(queryKey, (map) =>
                applyFollowChange(map, organizerId, action === 'follow'),
            );
            return { previous };
        },

        onError: (error, variables, context) => {
            // Roll back to the pre-mutation snapshot.
            if (context?.previous !== undefined) {
                queryClient.setQueryData(queryKey, context.previous);
            }
            options.onError?.(variables, error);
        },

        onSuccess: (result, { organizerId, action }, context) => {
            // The optimistic update in onMutate already applied the expected
            // change. Only reconcile if the server DISAGREES with the optimistic
            // assumption (avoids double-counting the follower delta): rebuild
            // from the pre-mutation snapshot using the server's authoritative
            // followedByYou.
            const optimisticFollowed = action === 'follow';
            if (result.followedByYou !== optimisticFollowed) {
                queryClient.setQueryData<OrganizerDataMap>(queryKey, () =>
                    applyFollowChange(
                        context?.previous,
                        organizerId,
                        result.followedByYou,
                    ),
                );
            }
            if (action === 'follow') {
                options.onFollowSuccess?.(organizerId);
            } else {
                options.onUnfollowSuccess?.(organizerId);
            }
        },
    });

    const follow = useCallback(
        (organizerId: string) => mutation.mutate({ organizerId, action: 'follow' }),
        [mutation],
    );
    const unfollow = useCallback(
        (organizerId: string) =>
            mutation.mutate({ organizerId, action: 'unfollow' }),
        [mutation],
    );

    return { mutation, follow, unfollow };
};
